const WINDOW_MS = 24 * 60 * 60 * 1000;

interface PriceSample {
  t: number;
  price: number;
}

interface VolumeSample {
  t: number;
  usd: number;
}

interface HistoryEntry {
  prices: PriceSample[];
  volumes: VolumeSample[];
}

function key(pairId: string) {
  return `pelagos-market-history-${pairId}`;
}

function load(pairId: string): HistoryEntry {
  try {
    const raw = localStorage.getItem(key(pairId));
    if (!raw) return { prices: [], volumes: [] };
    const parsed = JSON.parse(raw) as HistoryEntry;
    return { prices: parsed.prices ?? [], volumes: parsed.volumes ?? [] };
  } catch {
    return { prices: [], volumes: [] };
  }
}

function save(pairId: string, entry: HistoryEntry) {
  const cutoff = Date.now() - WINDOW_MS;
  const pruned: HistoryEntry = {
    prices: entry.prices.filter((p) => p.t >= cutoff),
    volumes: entry.volumes.filter((v) => v.t >= cutoff),
  };
  localStorage.setItem(key(pairId), JSON.stringify(pruned));
}

// records at most one sample per ~30s per pair, to keep localStorage small
export function recordPriceSample(pairId: string, price: number) {
  const entry = load(pairId);
  const now = Date.now();
  const last = entry.prices[entry.prices.length - 1];
  if (last && now - last.t < 30_000) return;
  entry.prices.push({ t: now, price });
  save(pairId, entry);
}

export function recordVolumeSample(pairId: string, usd: number) {
  const entry = load(pairId);
  entry.volumes.push({ t: Date.now(), usd });
  save(pairId, entry);
}

export function readMarketStats(pairId: string, currentPrice: number | undefined) {
  const { prices, volumes } = load(pairId);
  const cutoff = Date.now() - WINDOW_MS;
  const recentPrices = prices.filter((p) => p.t >= cutoff);
  const recentVolumes = volumes.filter((v) => v.t >= cutoff);

  const allPrices = currentPrice !== undefined ? [...recentPrices.map((p) => p.price), currentPrice] : recentPrices.map((p) => p.price);

  const high24h = allPrices.length ? Math.max(...allPrices) : currentPrice;
  const low24h = allPrices.length ? Math.min(...allPrices) : currentPrice;

  const oldest = recentPrices[0];
  const change24h =
    oldest && currentPrice !== undefined && oldest.price > 0
      ? ((currentPrice - oldest.price) / oldest.price) * 100
      : 0;

  const volume24h = recentVolumes.reduce((sum, v) => sum + v.usd, 0);

  return {
    high24h,
    low24h,
    change24h,
    volume24h,
    series: recentPrices.map((p) => ({ t: p.t, price: p.price })),
  };
}
